import React, { useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { type NativePrintProps, NativeReportShell } from '~/nativeLenses/NativePrintBoard';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import { Box, Button, CircularProgress, Tab, Tabs, Typography } from '@mui/material';

import { FONT_STACK_SUISSE_INTL } from '~/components/brand/brandTokens';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';
import type { PageField } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import { dateError } from '../ContributionMargin/liveModel';
import { download } from '../RmMonitor/liveModel';

import {
  ALL_DEALS,
  CLOSING_CARD,
  DEAL_CARD,
  type EndedFilter,
  LOCATION_CARD,
  LOCATION_FROM,
  LOCATION_TO,
  MARKET_CARD,
  NO_VENDOR,
  PAGE_ID,
  SAVED_FROM,
  SAVED_TO,
  TRAFFIC_CARD,
  VENDOR_DAILY_CARD,
  VIEWS,
  conversion,
  dealSummaryCsv,
  filterDeals,
  formatInteger,
  formatMoney,
  formatMoney2,
  formatPercent,
  grossSalesTotal,
  readClosing,
  readDaily,
  readDeals,
  readLocation,
  readMarket,
  readTraffic,
  revenuePerView,
  sortDeals,
  vendorRows,
} from './liveDataAdapter';

const SCHEMA: Record<string, PageField> = {
  view: { type: 'enum', values: [...VIEWS] },
  from: { type: 'date' },
  to: { type: 'date' },
  ended: { type: 'enum', values: ['all', 'yes', 'no'] },
  deal: { type: 'text', maxLength: 80 },
  vendor: { type: 'text', maxLength: 200 },
};

function rowsOf(cards: { cardId?: string; rows?: unknown }[] | undefined, cardId: string): Record<string, unknown>[] {
  const card = cards?.find((item) => item.cardId === cardId);
  return (card?.rows ?? []) as Record<string, unknown>[];
}

function readyOf(
  cards: { cardId?: string; status?: string; truncated?: boolean }[] | undefined,
  cardId: string,
): boolean {
  const card = cards?.find((item) => item.cardId === cardId);
  return (card?.status === 'ready' || card?.status === 'empty') && !card.truncated;
}

export default function HotelDealPerformancePage({ printTarget }: NativePrintProps) {
  return (
    <NativeReportShell
      pageId="hotel-deal-performance"
      title="Deal Performance for Hotel"
      printTarget={printTarget}
      analytics
    >
      <HotelDealPerformanceContent />
    </NativeReportShell>
  );
}


function HotelDealPerformanceContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const view = VIEWS.includes(params.get('view') as (typeof VIEWS)[number])
    ? (params.get('view') as (typeof VIEWS)[number])
    : VIEWS[0];
  const ended = (['all', 'yes', 'no'].includes(params.get('ended') ?? '') ? params.get('ended') : 'no') as EndedFilter;
  const isLocation = view === 'Location Results';
  const fromKey = isLocation ? 'locationFrom' : 'from';
  const toKey = isLocation ? 'locationTo' : 'to';
  const from = params.get(fromKey) || (isLocation ? LOCATION_FROM : SAVED_FROM);
  const to = params.get(toKey) || (isLocation ? LOCATION_TO : SAVED_TO);
  const isClosing = view === 'Deals Closing View';
  const invalidDates = isClosing ? null : dateError(from, to);
  const selectedDeal = params.get('deal') || null;
  const selectedVendor = params.get('vendor') || null;

  const setParams = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(window.location.search);
    Object.entries(changes).forEach(([key, value]) => {
      if (!value) next.delete(key);
      else next.set(key, value);
    });
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  };

  const query = useGetReportingLeLensData(
    invalidDates ? undefined : nativeLensId(PAGE_ID),
    {
      deal: selectedDeal ?? ALL_DEALS,
      vendor: selectedVendor ?? NO_VENDOR,
      ...(!isClosing ? { period: { window: { from, to }, comparison: null } } : {}),
    },
    false,
  );
  const cards = query.data?.cards;
  const allDeals = useMemo(() => sortDeals(readDeals(rowsOf(cards, DEAL_CARD))), [cards]);
  const deals = useMemo(() => filterDeals(allDeals, ended), [allDeals, ended]);
  const traffic = useMemo(() => readTraffic(rowsOf(cards, TRAFFIC_CARD)), [cards]);
  const market = useMemo(() => readMarket(rowsOf(cards, MARKET_CARD)), [cards]);
  const closing = useMemo(() => readClosing(rowsOf(cards, CLOSING_CARD)), [cards]);
  const daily = useMemo(() => readDaily(rowsOf(cards, VENDOR_DAILY_CARD)), [cards]);
  const locationRows = rowsOf(cards, LOCATION_CARD);
  const countries = useMemo(() => readLocation(locationRows, 'country'), [locationRows]);
  const regions = useMemo(() => readLocation(locationRows, 'region'), [locationRows]);
  const currencies = useMemo(() => readLocation(locationRows, 'currency'), [locationRows]);
  const vendors = useMemo(() => vendorRows(allDeals), [allDeals]);
  const vendorDeals = useMemo(
    () => (selectedVendor ? allDeals.filter((row) => row.vendor === selectedVendor) : []),
    [allDeals, selectedVendor],
  );
  const requiredCards = {
    'Deal Performance View': [DEAL_CARD, TRAFFIC_CARD, MARKET_CARD],
    'Location Results': [LOCATION_CARD],
    'Property Run History View': selectedVendor ? [DEAL_CARD, VENDOR_DAILY_CARD] : [DEAL_CARD],
    'Deals Closing View': [CLOSING_CARD],
  }[view];
  const ready = !invalidDates && !query.isShowingPreviousData && requiredCards.every((cardId) => readyOf(cards, cardId));
  const loading = (query.isLoading || query.isFetching) && !ready;
  const failed = !invalidDates && !loading && (query.isError || Boolean(cards && !ready));
  const filters = { view, from, to, ended, deal: selectedDeal ?? '', vendor: selectedVendor ?? '' };
  const summaryByView = {
    'Deal Performance View': {
      scope: `Transaction dates ${from} to ${to}. Ended-deals filtering applies to the deal table and its gross-sales total. Customer breakdowns cover the selected deal, or all deals when none is selected.`,
      grossSales: grossSalesTotal(deals),
      rows: deals.length,
      displayedRows: Math.min(deals.length, 80),
      customerBreakdown: {
        selectedDeal,
        grossSales: traffic.reduce((total, row) => total + row.grossSales, 0),
        customerRegions: traffic.length,
        displayedCustomerRegions: Math.min(traffic.length, 20),
        markets: market.length,
      },
    },
    'Location Results': {
      scope: `Point-of-supply transaction dates ${from} to ${to}. Country, region and currency are separate aggregations of the same sales; do not add their totals together.`,
      primaryBreakdown: 'country',
      grossSales: countries.reduce((total, row) => total + row.grossSales, 0),
      countries: countries.length,
      displayedCountries: Math.min(countries.length, 40),
      regions: regions.length,
      displayedRegions: Math.min(regions.length, 40),
      currencies: currencies.length,
    },
    'Property Run History View': {
      scope: `Transaction dates ${from} to ${to}. Vendor selection controls the daily-sales and deal-history tables. Deal totals and daily-sales totals are separate extracts, not additive.`,
      selectedVendor,
      vendors: vendors.length,
      displayedVendors: Math.min(vendors.length, 80),
      dealGrossSales: selectedVendor ? grossSalesTotal(vendorDeals) : null,
      deals: vendorDeals.length,
      dailyGrossSales: selectedVendor ? daily.reduce((total, row) => total + row.grossSales, 0) : null,
      dailyRows: selectedVendor ? daily.length : 0,
      selectionRequired: !selectedVendor,
    },
    'Deals Closing View': {
      scope:
        'Deal end dates from 4 days before through 15 days after today in Australia/Sydney. Sales cover all transaction dates; saved historical transaction dates do not apply.',
      grossSales: closing.reduce((total, row) => total + row.grossSales, 0),
      rows: closing.length,
      displayedRows: Math.min(closing.length, 80),
    },
  };
  const activeFilters = {
    'Deal Performance View': ['view', 'from', 'to', 'ended', 'deal'],
    'Location Results': ['view', 'from', 'to'],
    'Property Run History View': ['view', 'from', 'to', 'vendor'],
    'Deals Closing View': ['view'],
  }[view];

  usePageBridge({
    pageId: PAGE_ID,
    view: {
      title: `Deal Performance for Hotel · ${view}`,
      ready: ready && !query.isShowingPreviousData,
      filterSchema: SCHEMA,
      filters,
      filterScope: {
        active: activeFilters,
        inactive: Object.keys(filters).filter((key) => !activeFilters.includes(key)),
        inactiveValues: 'Retained for other tabs; they do not filter the current view.',
        ended: 'Deal table and its headline total only.',
        deal: 'Customer-region and domestic/international breakdowns only.',
        vendor: 'Property daily-sales and deal-history tables only.',
      },
      dataMode: 'live BigQuery through Lens',
      summary: {
        worksheet: view,
        ...summaryByView[view],
        complete: ready && !query.isShowingPreviousData,
      },
      definitions: {
        grossSales:
          'Gross Sales AUD is SUM of value where IsRefund is No, for Hotel rows with payment sequence 1, excluding cancelled and null item status.',
        countdown:
          'Countdown Days is the day difference from today in Australia/Sydney to the deal end date. A blank end date is 0.',
        daysLive: 'Days Live is the day difference from the deal start date to yesterday in Australia/Sydney.',
        pageViews: 'Page Views are SUM of reporting.events view_qty on dates the deal has a non-refund sale.',
        conversion:
          'Conversion is COUNTD of non-refund, non-surcharge, non-cancelled item_bk divided by SUM of unique page views.',
        orders: 'Total Orders is COUNT DISTINCT order_id, already aggregated per deal. The page sums that count.',
      },
    },
    edit: (changes) => {
      if (!ready) throw new Error('Page data is still loading.');
      validatePageFilters(changes, SCHEMA);
      if ((changes.view ?? view) === 'Deals Closing View' && ('from' in changes || 'to' in changes)) {
        throw new Error('Transaction dates do not apply to Deals Closing.');
      }
      const targetLocation = (changes.view ?? view) === 'Location Results';
      const targetFromKey = targetLocation ? 'locationFrom' : 'from';
      const targetToKey = targetLocation ? 'locationTo' : 'to';
      const nextFrom = String(changes.from ?? params.get(targetFromKey) ?? (targetLocation ? LOCATION_FROM : SAVED_FROM));
      const nextTo = String(changes.to ?? params.get(targetToKey) ?? (targetLocation ? LOCATION_TO : SAVED_TO));
      const problem = (changes.view ?? view) === 'Deals Closing View' ? null : dateError(nextFrom, nextTo);
      if (problem) throw new Error(problem);
      const nextEnded = ('ended' in changes ? changes.ended : ended) as EndedFilter;
      if (changes.deal && !filterDeals(allDeals, nextEnded).slice(0, 80).some((row) => row.dealId === changes.deal)) {
        throw new Error('Deal must be listed in the deal table.');
      }
      if (changes.vendor && !vendors.slice(0, 80).some((row) => row.vendor === changes.vendor)) {
        throw new Error('Vendor must be listed in the vendor table.');
      }
      setParams({
        ...(changes.from !== undefined ? { [targetFromKey]: nextFrom } : {}),
        ...(changes.to !== undefined ? { [targetToKey]: nextTo } : {}),
        view:
          'view' in changes
            ? changes.view === VIEWS[0]
              ? null
              : String(changes.view ?? '')
            : view === VIEWS[0]
              ? null
              : view,
        ended:
          'ended' in changes
            ? changes.ended === 'no'
              ? null
              : String(changes.ended ?? '')
            : ended === 'no'
              ? null
              : ended,
        deal: 'deal' in changes ? String(changes.deal ?? '') || null : selectedDeal,
        vendor: 'vendor' in changes ? String(changes.vendor ?? '') || null : selectedVendor,
      });
    },
  });

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2 },
        display: 'grid',
        gap: 2,
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
        fontFamily: FONT_STACK_SUISSE_INTL,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1,
          minWidth: 0,
        }}
      >
        <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          <Typography
            component="h1"
            variant="h5"
            sx={{ fontFamily: FONT_STACK_SUISSE_INTL, fontSize: { xs: 22, sm: 24 } }}
          >
            Deal Performance for Hotel
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hotel sales, payment sequence 1.{' '}
            {view === 'Deals Closing View'
              ? 'Deal end dates: 4 days before through 15 days after today in Australia/Sydney. Sales cover all transaction dates.'
              : view === 'Location Results'
                ? `Point of supply: ${from} to ${to}.`
                : `Transaction dates: ${from} to ${to}.`}
          </Typography>
        </Box>
        <PageChatButton />
      </Box>
      <Tabs
        value={view}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ minWidth: 0, maxWidth: '100%' }}
        onChange={(_event, next: (typeof VIEWS)[number]) => setParams({ view: next === VIEWS[0] ? null : next })}
      >
        {VIEWS.map((item) => (
          <Tab key={item} value={item} label={item} />
        ))}
      </Tabs>
      {view !== 'Deals Closing View' ? (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <label>From <input aria-label="From" type="date" value={from} onChange={(event) => setParams({ [fromKey]: event.target.value })} /></label>
          <label>To <input aria-label="To" type="date" value={to} onChange={(event) => setParams({ [toKey]: event.target.value })} /></label>
        </Box>
      ) : null}
      {invalidDates && view !== 'Deals Closing View' ? <Typography>{invalidDates}</Typography> : null}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}
      {failed ? <Typography>This extract is not available.</Typography> : null}
      {ready && view === 'Deal Performance View' ? (
        <DealView
          deals={deals}
          traffic={traffic}
          market={market}
          ended={ended}
          selectedDeal={selectedDeal}
          from={from}
          to={to}
          onEnded={(value) => setParams({ ended: value === 'no' ? null : value })}
          onDeal={(dealId) => setParams({ deal: selectedDeal === dealId ? null : dealId })}
        />
      ) : null}
      {ready && view === 'Location Results' ? (
        <LocationView countries={countries} regions={regions} currencies={currencies} from={from} to={to} />
      ) : null}
      {ready && view === 'Property Run History View' ? (
        <PropertyView
          vendors={vendors}
          selectedVendor={selectedVendor}
          vendorDeals={vendorDeals}
          daily={daily}
          onVendor={(vendor) => setParams({ vendor: selectedVendor === vendor ? null : vendor })}
        />
      ) : null}
      {ready && view === 'Deals Closing View' ? <ClosingView rows={closing} /> : null}
    </Box>
  );
}

function ExportButton({ name, content }: { name: string; content: string }) {
  return (
    <Button
      variant="outlined"
      size="small"
      sx={{ justifySelf: 'start', maxWidth: '100%', whiteSpace: 'normal', flexShrink: 0 }}
      onClick={() => download(name, content)}
    >
      Export CSV
    </Button>
  );
}

function DealView({
  deals,
  traffic,
  market,
  ended,
  selectedDeal,
  from,
  to,
  onEnded,
  onDeal,
}: {
  deals: ReturnType<typeof sortDeals>;
  traffic: ReturnType<typeof readTraffic>;
  market: ReturnType<typeof readMarket>;
  ended: EndedFilter;
  selectedDeal: string | null;
  from: string;
  to: string;
  onEnded: (value: EndedFilter) => void;
  onDeal: (dealId: string) => void;
}) {
  return (
    <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
        <Typography variant="body2">Show ended deals</Typography>
        {(['all', 'no', 'yes'] as const).map((value) => (
          <Button
            key={value}
            size="small"
            aria-pressed={ended === value}
            variant={ended === value ? 'contained' : 'text'}
            onClick={() => onEnded(value)}
          >
            {value === 'all' ? 'All' : value === 'no' ? 'No' : 'Yes'}
          </Button>
        ))}
        <ExportButton name="deal-summary.csv" content={dealSummaryCsv(deals)} />
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          Gross sales {formatMoney(grossSalesTotal(deals))}
        </Typography>
      </Box>
      <Table
        headers={[
          'Opportunity',
          'Deal',
          'Offer Region',
          'Tier',
          'Countdown',
          'Days Live',
          'Gross Sales',
          'Page Views',
          'Revenue/View',
          'Conversion',
        ]}
        rows={deals.slice(0, 80).map((row) => ({
          key: row.dealId,
          selected: row.dealId === selectedDeal,
          onClick: () => onDeal(row.dealId),
          cells: [
            row.opportunity,
            row.dealId,
            row.offerRegion,
            row.dealTier,
            formatInteger(row.countdown),
            formatInteger(row.daysLive),
            formatMoney(row.grossSales),
            formatInteger(row.pageViews),
            formatMoney2(revenuePerView(row.grossSales, row.pageViews)),
            formatPercent(conversion(row.packages, row.uniqueViews)),
          ],
        }))}
      />
      <Typography variant="body2" color="text.secondary">
        Customer breakdowns: {selectedDeal ? `selected deal ${selectedDeal}` : 'all deals'}, {from} to {to}.{' '}
        Show ended deals applies only to the deal table and gross-sales total above.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.4fr) minmax(0, 0.8fr)' },
          gap: 2,
          minWidth: 0,
        }}
      >
        <Box component="section" sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1">Customer region</Typography>
          <Table
            headers={['Customer Region', 'Gross Sales', 'Share', 'Orders', 'Order share']}
            rows={traffic.slice(0, 20).map((row) => ({
              key: row.customerRegion,
              cells: [
                row.customerRegion,
                formatMoney(row.grossSales),
                formatPercent(row.share),
                formatInteger(row.totalOrders),
                formatPercent(row.orderShare),
              ],
            }))}
          />
        </Box>
        <Box component="section" sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1">Domestic vs international</Typography>
          <Table
            headers={['Market', 'Gross Sales', 'Share']}
            rows={market.map((row) => ({
              key: row.market,
              cells: [row.market, formatMoney(row.grossSales), formatPercent(row.share)],
            }))}
          />
        </Box>
      </Box>
    </Box>
  );
}

function LocationView({
  countries,
  regions,
  currencies,
  from,
  to,
}: {
  from: string;
  to: string;
  countries: ReturnType<typeof readLocation>;
  regions: ReturnType<typeof readLocation>;
  currencies: ReturnType<typeof readLocation>;
}) {
  const place = (row: (typeof countries)[number]) => [
    row.name,
    formatMoney(row.grossSales),
    formatMoney(row.refunds),
    formatMoney(row.grossMargin),
    formatMoney(row.vendorRevenue),
    formatInteger(row.totalOrders),
  ];
  return (
    <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
      <ExportButton
        name="location-country.csv"
        content={[
          'Country,Gross Sales,Refunds',
          ...countries.map((row) => `${csvCell(row.name)},${row.grossSales},${row.refunds}`),
        ].join('\r\n')}
      />
      <Typography variant="body2" color="text.secondary">
        Point of supply, {from} to {to}
      </Typography>
      <Typography variant="subtitle1">Point of supply country</Typography>
      <Table
        headers={['Country', 'Gross Sales', 'Refunds', 'Gross Margin', 'Vendor Revenue', 'Orders']}
        rows={countries.slice(0, 40).map((row) => ({ key: row.name, cells: place(row) }))}
      />
      <Typography variant="subtitle1">Point of supply region</Typography>
      <Table
        headers={['Region', 'Gross Sales', 'Refunds', 'Gross Margin', 'Vendor Revenue', 'Orders']}
        rows={regions.slice(0, 40).map((row) => ({ key: row.name, cells: place(row) }))}
      />
      <Typography variant="subtitle1">Currency</Typography>
      <Table
        headers={['Currency', 'Gross Sales', 'Refunds', 'Gross Margin', 'Vendor Revenue', 'Orders']}
        rows={currencies.map((row) => ({ key: row.name, cells: place(row) }))}
      />
    </Box>
  );
}

function PropertyView({
  vendors,
  selectedVendor,
  vendorDeals,
  daily,
  onVendor,
}: {
  vendors: ReturnType<typeof vendorRows>;
  selectedVendor: string | null;
  vendorDeals: ReturnType<typeof sortDeals>;
  daily: ReturnType<typeof readDaily>;
  onVendor: (vendor: string) => void;
}) {
  let running = 0;
  const cumulative = [...vendorDeals]
    .sort((left, right) => left.startDate.localeCompare(right.startDate) || left.dealId.localeCompare(right.dealId))
    .map((row) => {
      running += row.grossSales;
      return { ...row, cumulative: running };
    });
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
        gap: 2,
        minWidth: 0,
      }}
    >
      <Box component="section" sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1">Vendors</Typography>
        <Table
          headers={['Vendor', 'Most recent start', 'Deals run', 'Gross Sales']}
          rows={vendors.slice(0, 80).map((row) => ({
            key: row.vendor,
            selected: row.vendor === selectedVendor,
            onClick: () => onVendor(row.vendor),
            cells: [row.vendor, row.mostRecentStart, formatInteger(row.dealsRun), formatMoney(row.grossSales)],
          }))}
        />
      </Box>
      <Box component="section" sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ overflowWrap: 'anywhere' }}>
          {selectedVendor ? selectedVendor : 'Select a vendor'}
        </Typography>
        {selectedVendor ? (
          <>
            <Table
              headers={['Date', 'Gross Sales', 'Cumulative']}
              rows={daily.map((row) => ({
                key: row.saleDate,
                cells: [row.saleDate, formatMoney(row.grossSales), formatMoney(row.cumulativeSales)],
              }))}
            />
            <Table
              headers={['Deal', 'Start', 'Gross Sales', 'Cumulative']}
              rows={cumulative.map((row) => ({
                key: row.dealId,
                cells: [row.dealId, row.startDate, formatMoney(row.grossSales), formatMoney(row.cumulative)],
              }))}
            />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Property run history stays empty until a vendor is selected.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function ClosingView({ rows }: { rows: ReturnType<typeof readClosing> }) {
  return (
    <Box sx={{ display: 'grid', gap: 1, minWidth: 0 }}>
      <ExportButton
        name="deals-closing.csv"
        content={[
          'Vendor,Offer Country,End Date,Gross Sales',
          ...rows.map((row) => `${csvCell(row.vendor)},${csvCell(row.offerCountry)},${row.endDate},${row.grossSales}`),
        ].join('\r\n')}
      />
      <Table
        headers={[
          '#',
          'Vendor',
          'Country',
          'End',
          'Gross Sales',
          'Refunds',
          'Margin',
          'Vendor Revenue',
          'Net Orders',
          'Nights',
        ]}
        rows={rows.slice(0, 80).map((row) => ({
          key: `${row.vendor}-${row.offerCountry}-${row.endDate}`,
          cells: [
            String(row.index),
            row.vendor,
            row.offerCountry,
            row.endDate,
            formatMoney(row.grossSales),
            formatMoney(row.refunds),
            formatMoney(row.grossMargin),
            formatMoney(row.vendorRevenue),
            formatInteger(row.netOrders),
            formatInteger(row.nights),
          ],
        }))}
      />
    </Box>
  );
}

function csvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function Table({
  headers,
  rows,
}: {
  headers: string[];
  rows: { key: string; cells: string[]; selected?: boolean; onClick?: () => void }[];
}) {
  return (
    <Box
      sx={{
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'auto',
        maxHeight: 420,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'inherit', fontSize: 13 }}>
        <thead>
          <tr>
            {headers.map((header) => (
              <Box
                component="th"
                key={header}
                sx={{
                  textAlign: 'left',
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  p: '6px 8px',
                  whiteSpace: 'nowrap',
                }}
              >
                {header}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Box
              component="tr"
              key={row.key}
              onClick={row.onClick}
              sx={{
                bgcolor: row.selected ? 'action.selected' : undefined,
                cursor: row.onClick ? 'pointer' : undefined,
              }}
            >
              {row.cells.map((cell, index) => (
                <td key={`${row.key}-${index}`} style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                  {cell}
                </td>
              ))}
            </Box>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}
